import { prefersReducedMotion } from '@theme/utilities';

class TiovivoCollage extends HTMLElement {
  connectedCallback() {
    if (this.#isConnected) return;

    this.#isConnected = true;
    this.#viewport = this.querySelector('[data-tiovivo-viewport]');
    this.#itemsLayer = this.querySelector('[data-tiovivo-items-layer]');
    this.#sourceItems = Array.from(this.querySelectorAll('[data-tiovivo-item]'));

    if (!this.#viewport || !this.#itemsLayer || !this.#sourceItems.length) return;

    this.#resizeObserver = new ResizeObserver(() => {
      this.#measure();
      this.#syncItemPool();
      this.#layoutItems();
    });

    this.#resizeObserver.observe(this.#viewport);

    if (this.dataset.pauseOnHover === 'true') {
      this.addEventListener('pointerenter', this.#handlePointerEnter);
      this.addEventListener('pointerleave', this.#handlePointerLeave);
    }

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('resize', this.#handleResize, { passive: true });

    this.#measure();
    this.#syncItemPool();
    this.#layoutItems();

    if (!prefersReducedMotion() && !this.hasAttribute('data-shopify-visual-preview')) {
      this.#start();
    }
  }

  disconnectedCallback() {
    this.#isConnected = false;
    this.#stop();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('resize', this.#handleResize);
  }

  /**
   * @type {ResizeObserver | null}
   */
  #resizeObserver = null;

  /**
   * @type {HTMLElement | null}
   */
  #viewport = null;

  /**
   * @type {HTMLElement | null}
   */
  #itemsLayer = null;

  /**
   * @type {HTMLElement[]}
   */
  #sourceItems = [];

  /**
   * @type {HTMLElement[]}
   */
  #orderedSourceItems = [];

  /**
   * @type {HTMLElement[]}
   */
  #items = [];

  /**
   * @type {Array<{
   *   baseX: number;
   *   depth: number;
   *   height: number;
   *   opacity: number;
   *   rotation: number;
   *   width: number;
   *   wrapCount: number;
   *   x: number;
   *   y: number;
   * }>}
   */
  #states = [];

  /**
   * @type {number}
   */
  #viewportWidth = 0;

  /**
   * @type {number}
   */
  #viewportHeight = 0;

  /**
   * @type {number}
   */
  #speed = 0;

  /**
   * @type {number}
   */
  #gap = 0;

  /**
   * @type {number}
   */
  #edgeBleed = 0;

  /**
   * @type {number}
   */
  #foregroundOpacity = 1;

  /**
   * @type {number}
   */
  #backgroundOpacity = 0.74;

  /**
   * @type {number}
   */
  #loopLength = 0;

  /**
   * @type {number}
   */
  #sequenceStart = 0;

  /**
   * @type {number}
   */
  #sequenceEnd = 0;

  /**
   * @type {boolean}
   */
  #isMobile = false;

  /**
   * @type {number}
   */
  #rafId = 0;

  /**
   * @type {number}
   */
  #lastTimestamp = 0;

  /**
   * @type {boolean}
   */
  #isPaused = false;

  /**
   * @type {boolean}
   */
  #isConnected = false;

  #handlePointerEnter = () => {
    this.#isPaused = true;
  };

  #handlePointerLeave = () => {
    this.#isPaused = false;
    this.#lastTimestamp = performance.now();
    this.#start();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#stop();
      return;
    }

    if (!prefersReducedMotion() && !this.hasAttribute('data-shopify-visual-preview')) {
      this.#lastTimestamp = performance.now();
      this.#start();
    }
  };

  #handleResize = () => {
    this.#measure();
    this.#syncItemPool();
    this.#layoutItems();
  };

  #start() {
    if (this.#rafId || !this.#states.length) return;

    this.#lastTimestamp = performance.now();
    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #stop() {
    if (!this.#rafId) return;

    cancelAnimationFrame(this.#rafId);
    this.#rafId = 0;
  }

  #tick = (timestamp) => {
    this.#rafId = 0;

    if (this.#isPaused) {
      this.#start();
      return;
    }

    const deltaSeconds = Math.min((timestamp - this.#lastTimestamp) / 1000, 0.05);
    this.#lastTimestamp = timestamp;

    for (let index = 0; index < this.#states.length; index += 1) {
      const state = this.#states[index];
      state.x -= this.#speed * deltaSeconds;

      if (state.x + state.width < this.#sequenceStart - this.#edgeBleed) {
        const maxRight = this.#getMaxRight(index);
        state.x = maxRight + this.#gap;
        state.wrapCount += 1;
      }

      this.#applyState(this.#items[index], state);
    }

    this.#start();
  };

  #measure() {
    if (!this.#viewport) return;

    this.#viewportWidth = this.#viewport.clientWidth;
    this.#viewportHeight = this.#viewport.clientHeight;
    this.#isMobile = window.matchMedia('(max-width: 749px)').matches;

    const dataset = this.dataset;
    const speedValue = this.#isMobile ? dataset.speedMobile : dataset.speedDesktop;
    const edgeBleedValue = this.#isMobile ? dataset.edgeBleedMobile : dataset.edgeBleedDesktop;

    this.#speed = Number(speedValue || 34);
    this.#gap = Number(dataset.gap || 16);
    this.#edgeBleed = Number(edgeBleedValue || 120);
    this.#foregroundOpacity = Number(dataset.foregroundOpacity || 100) / 100;
    this.#backgroundOpacity = Number(dataset.backgroundOpacity || 74) / 100;
  }

  #syncItemPool() {
    if (!this.#itemsLayer || !this.#sourceItems.length) return;

    const clones = Array.from(this.#itemsLayer.querySelectorAll('[data-tiovivo-clone="true"]'));

    for (const clone of clones) {
      clone.remove();
    }

    this.#orderedSourceItems = [...this.#sourceItems].sort((itemA, itemB) => this.#getConfiguredX(itemA) - this.#getConfiguredX(itemB));

    const desiredCount = this.#getDesiredItemCount();
    const nextItems = [...this.#orderedSourceItems];

    while (nextItems.length < desiredCount) {
      const template = this.#orderedSourceItems[nextItems.length % this.#orderedSourceItems.length];
      const clone = /** @type {HTMLElement} */ (template.cloneNode(true));

      clone.dataset.tiovivoClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');

      for (const attribute of Array.from(clone.attributes)) {
        if (attribute.name.startsWith('data-shopify')) {
          clone.removeAttribute(attribute.name);
        }
      }

      this.#itemsLayer.append(clone);
      nextItems.push(clone);
    }

    this.#items = nextItems;
  }

  #layoutItems() {
    if (!this.#items.length || !this.#orderedSourceItems.length) return;

    const sourceStates = this.#orderedSourceItems.map((item) => this.#createBaseState(item));
    const sourceMinX = Math.min(...sourceStates.map((state) => state.baseX));
    const sourceMaxRight = Math.max(...sourceStates.map((state) => state.baseX + state.width));

    this.#sequenceStart = sourceMinX;
    this.#sequenceEnd = sourceMaxRight;
    this.#loopLength = Math.max(sourceMaxRight - sourceMinX + this.#gap, this.#viewportWidth * 0.4);

    this.#states = this.#items.map((item, index) => {
      const sourceState = sourceStates[index % sourceStates.length];
      const cycle = Math.floor(index / sourceStates.length);
      const state = {
        ...sourceState,
        wrapCount: cycle,
        x: sourceState.baseX + cycle * this.#loopLength,
      };

      this.#applyState(item, state);
      return state;
    });

    if (prefersReducedMotion() || this.hasAttribute('data-shopify-visual-preview')) {
      return;
    }

    this.#start();
  }

  #getDesiredItemCount() {
    if (!this.#orderedSourceItems.length) return this.#sourceItems.length;

    const estimatedStates = this.#orderedSourceItems.map((item) => this.#createBaseState(item));
    const sourceMinX = Math.min(...estimatedStates.map((state) => state.baseX));
    const sourceMaxRight = Math.max(...estimatedStates.map((state) => state.baseX + state.width));
    const loopLength = Math.max(sourceMaxRight - sourceMinX + this.#gap, this.#viewportWidth * 0.4);
    const cyclesNeeded = Math.max(2, Math.ceil((this.#viewportWidth + this.#edgeBleed * 2 + loopLength) / loopLength));

    return this.#orderedSourceItems.length * cyclesNeeded;
  }

  /**
   * @param {HTMLElement} item
   * @returns {{
   *   baseX: number;
   *   depth: number;
   *   height: number;
   *   opacity: number;
   *   rotation: number;
   *   width: number;
   *   y: number;
   * }}
   */
  #createBaseState(item) {
    const width = this.#getConfiguredWidth(item);
    const aspectRatio = this.#getAspectRatio(item);
    const height = width / aspectRatio;
    const x = this.#getConfiguredX(item);
    const y = this.#getConfiguredY(item);
    const layerPreference = item.dataset.layerPreference || 'auto';
    const rotation = this.#getConfiguredRotation(item);
    const isForeground = layerPreference === 'foreground';

    return {
      baseX: x,
      depth: isForeground ? 3 : 1,
      height,
      opacity: isForeground ? this.#foregroundOpacity : this.#backgroundOpacity,
      rotation,
      width,
      y: this.#clamp(y, -height, this.#viewportHeight),
    };
  }

  /**
   * @param {HTMLElement} item
   * @returns {number}
   */
  #getConfiguredX(item) {
    const rawValue = this.#isMobile ? item.dataset.xMobile : item.dataset.xDesktop;
    return (this.#viewportWidth * Number(rawValue || 0)) / 100;
  }

  /**
   * @param {HTMLElement} item
   * @returns {number}
   */
  #getConfiguredY(item) {
    const rawValue = this.#isMobile ? item.dataset.yMobile : item.dataset.yDesktop;
    return (this.#viewportHeight * Number(rawValue || 0)) / 100;
  }

  /**
   * @param {HTMLElement} item
   * @returns {number}
   */
  #getConfiguredWidth(item) {
    const rawValue = this.#isMobile ? item.dataset.widthMobile : item.dataset.widthDesktop;
    return Math.max(40, (this.#viewportWidth * Number(rawValue || 18)) / 100);
  }

  /**
   * @param {HTMLElement} item
   * @returns {number}
   */
  #getConfiguredRotation(item) {
    const rawValue = item.dataset.rotation;
    return Number(rawValue || 0);
  }

  /**
   * @param {HTMLElement} item
   * @returns {number}
   */
  #getAspectRatio(item) {
    const rawRatio = Number(item.dataset.imageRatio || 0.8);
    return this.#clamp(rawRatio, 0.3, 3);
  }

  /**
   * @param {HTMLElement} item
   * @param {{
   *   depth: number;
   *   height: number;
   *   opacity: number;
   *   rotation: number;
   *   width: number;
   *   x: number;
   *   y: number;
   * }} state
   */
  #applyState(item, state) {
    item.style.setProperty('--tiovivo-item-width', `${state.width}px`);
    item.style.setProperty('--tiovivo-item-height', `${state.height}px`);
    item.style.setProperty('--tiovivo-item-x', `${state.x}px`);
    item.style.setProperty('--tiovivo-item-y', `${state.y}px`);
    item.style.setProperty('--tiovivo-item-rotation', `${state.rotation}deg`);
    item.style.setProperty('--tiovivo-item-opacity', `${state.opacity}`);
    item.style.setProperty('--tiovivo-item-depth', `${state.depth}`);
  }

  /**
   * @param {number} skipIndex
   * @returns {number}
   */
  #getMaxRight(skipIndex) {
    let maxRight = this.#sequenceEnd;

    for (let index = 0; index < this.#states.length; index += 1) {
      if (index === skipIndex) continue;

      const state = this.#states[index];
      maxRight = Math.max(maxRight, state.x + state.width);
    }

    return maxRight;
  }

  /**
   * @param {number} value
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  #clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
}

if (!customElements.get('tiovivo-collage')) {
  customElements.define('tiovivo-collage', TiovivoCollage);
}
